import { watch } from 'vue'
import type { PipeIntegration, PipeIntegrationDef } from '..'
import { PIPE_EVENTS, type PipeInterface } from '@render/composables/usePipe'
import { getAgeDecade } from '@render/lib/ageCalc'
import { formatLocalIsoDate, getDaysUntil } from '@render/lib/dateCalc'
import { parseNumericVar } from '@render/lib/parseNumericVar'
import {
  GOAL_PACE_KG_VALUES,
  clampGoalPaceKg,
  getBellyLegsIn2Weeks,
  getBmi,
  getDaysTillGoalDate,
  getDesiredWeightDiffKg,
  getFasterProgressDate,
  getFasterProgressDate1kg,
  getFirstWeekDiffKg,
  getFirstWeekLossKg,
  getGoalDate,
  getGoalDate1kg,
  getMaxSuggestedWeightKg,
  getMinSuggestedWeightKg,
  getMonthsTillGoalDate,
  getOneMonthWeightLossKg,
  getSuggestedWeightKg,
  getWeightIn2WeeksKg,
  percentOfCurrentWeight,
  type DaysTillGoalInput,
} from '@render/lib/weightCalc'
import { getDailyKcalLeftover, getDailyKcalRange } from './dailyKcal'
import {
  computeEventDate,
  resolveDaysTillGoalDateParams,
  resolveEventDateParams,
  type FitnessFunctionsConfig,
} from './functionOverrides'
import { GOAL_DATE_TICK_STEP_MS, buildGoalDateTickFrames } from './goalDateTick'

const HEIGHT_KEY = 'var.global.heightCm'
const WEIGHT_KEY = 'var.global.weightKg'
const TARGET_WEIGHT_KEY = 'var.global.targetWeightKg'
const SUGGESTED_TARGET_WEIGHT_KEY = 'var.global.suggestedTargetWeightKg'
const MIN_SUGGESTED_WEIGHT_KEY = 'var.global.minSuggestedWeightKg'
const MAX_SUGGESTED_WEIGHT_KEY = 'var.global.maxSuggestedWeightKg'
const GOAL_PACE_KEY = 'var.global.goalPaceKg'
const CURRENT_BMI_KEY = 'var.global.currentBmi'
const TARGET_BMI_KEY = 'var.global.targetBmi'
const TARGET_GOAL_DATE_KEY = 'var.global.targetGoalDate'
const TARGET_GOAL_DATE_1KG_KEY = 'var.global.targetGoalDate1kgPerWeek'
const DAYS_TILL_GOAL_DATE_KEY = 'var.global.daysTillGoalDate'
const MONTHS_TILL_GOAL_DATE_KEY = 'var.global.monthsTillGoalDate'
const ONE_MONTH_WEIGHT_LOSS_KG_KEY = 'var.global.oneMonthWeightLossKg'
const DESIRED_WEIGHT_DIFF_KG_KEY = 'var.global.desiredWeightDiffKg'
const FASTER_GOAL_DATE_KEY = 'var.global.fasterGoalDate'
const FASTER_GOAL_DATE_1KG_KEY = 'var.global.fasterGoalDate1kgPerWeek'
const FIRST_WEEK_LOSS_KG_KEY = 'var.global.firstWeekLossKg'
const FIRST_WEEK_DIFF_KG_KEY = 'var.global.firstWeekDiffKg'
const PERCENT_OF_CURRENT_WEIGHT_KEY = 'var.global.percentOfCurrentWeight'
const WEIGHT_IN_2_WEEKS_KG_KEY = 'var.global.weightIn2Weeks'
const BELLY_IN_2_WEEKS_KEY = 'var.global.bellyIn2Weeks'
const LEGS_IN_2_WEEKS_KEY = 'var.global.legsIn2Weeks'
const GENDER_KEY = 'var.global.gender'
const AGE_KEY = 'var.global.age'
const AGE_GROUP_DECADE_KEY = 'var.global.ageGroupDecade'
const DAILY_KCAL_KEY = 'var.global.dailyKcal'
const DAILY_KCAL_MIN_KEY = 'var.global.dailyKcalMin'
const DAILY_KCAL_MAX_KEY = 'var.global.dailyKcalMax'
const DAILY_KCAL_LEFTOVER_MIN_KEY = 'var.global.dailyKcalLeftoverMin'
const DAILY_KCAL_LEFTOVER_MAX_KEY = 'var.global.dailyKcalLeftoverMax'
const COMPLETEMEALS_COVER_KEY = 'var.global.completemealsCover'
const EVENT_DATE_KEY = 'var.global.eventDate'
const DAYS_UNTIL_EVENT_KEY = 'var.global.daysUntilEvent'
const WEEKS_UNTIL_EVENT_KEY = 'var.global.weeksUntilEvent'
const MONTHS_UNTIL_EVENT_KEY = 'var.global.monthsUntilEvent'
const WEIGHT_LOST_BY_EVENT_KG_KEY = 'var.global.weightLostByEventKg'

const useFitnessFunctionsIntegration: PipeIntegration = (): PipeIntegrationDef => ({
  setup(pipe: PipeInterface, config: FitnessFunctionsConfig | null | undefined) {
    const lossPace = resolveDaysTillGoalDateParams(config)
    const eventDateParams = resolveEventDateParams(config)

    watch(
      () => pipe.getData(HEIGHT_KEY),
      (raw) => {
        const heightCm = Number(raw)
        if (!Number.isFinite(heightCm) || heightCm <= 0) return
        pipe.setData(SUGGESTED_TARGET_WEIGHT_KEY, getSuggestedWeightKg({ heightCm }))
      },
      { immediate: true },
    )

    watch(
      () => pipe.getData(GOAL_PACE_KEY),
      (raw) => {
        const n = Number(raw)
        if (!Number.isFinite(n) || n <= 0) return
        if ((GOAL_PACE_KG_VALUES as readonly number[]).includes(n)) return
        pipe.setData(GOAL_PACE_KEY, clampGoalPaceKg(n))
      },
    )

    watch(
      () => [pipe.getData(WEIGHT_KEY), pipe.getData(HEIGHT_KEY)],
      ([w, h]) => {
        const bmi = getBmi({ weightKg: Number(w), heightCm: Number(h) })
        if (bmi === null) return
        pipe.setData(CURRENT_BMI_KEY, bmi)
      },
      { immediate: true },
    )

    watch(
      () => [pipe.getData(TARGET_WEIGHT_KEY), pipe.getData(HEIGHT_KEY)],
      ([w, h]) => {
        const bmi = getBmi({ weightKg: Number(w), heightCm: Number(h) })
        if (bmi === null) return
        pipe.setData(TARGET_BMI_KEY, bmi)
      },
      { immediate: true },
    )

    watch(
      () => pipe.getData(HEIGHT_KEY),
      (raw) => {
        const min = getMinSuggestedWeightKg({ heightCm: Number(raw) })
        if (min !== null) pipe.setData(MIN_SUGGESTED_WEIGHT_KEY, min)
      },
      { immediate: true },
    )

    watch(
      () => [pipe.getData(HEIGHT_KEY), pipe.getData(WEIGHT_KEY)],
      ([h, w]) => {
        const max = getMaxSuggestedWeightKg({
          heightCm: Number(h),
          weightKg: Number(w),
        })
        if (max !== null) pipe.setData(MAX_SUGGESTED_WEIGHT_KEY, max)
      },
      { immediate: true },
    )

    watch(
      () => [
        pipe.getData(WEIGHT_KEY),
        pipe.getData(TARGET_WEIGHT_KEY),
        pipe.getData(GOAL_PACE_KEY),
      ],
      ([w, t, p]) => {
        const weightKg = Number(w)
        const targetWeightKg = Number(t)
        if (!Number.isFinite(weightKg) || weightKg <= 0) return
        if (!Number.isFinite(targetWeightKg) || targetWeightKg <= 0) return
        const paceNum = Number(p)
        const goalPaceKg = Number.isFinite(paceNum) && paceNum > 0 ? paceNum : undefined
        const input = { weightKg, targetWeightKg, goalPaceKg, lossPace }
        pipe.setData(DAYS_TILL_GOAL_DATE_KEY, getDaysTillGoalDate(input))
        pipe.setData(MONTHS_TILL_GOAL_DATE_KEY, getMonthsTillGoalDate(input))
        // Dates are formatted on the visitor's local calendar (consistent with
        // daysTillGoalDate above) and skipped rather than written when the
        // computed Date is somehow invalid — `formatLocalIsoDate` returns null
        // instead of throwing the way `toISOString()` does.
        const goalDate = formatLocalIsoDate(getGoalDate(input))
        if (goalDate) pipe.setData(TARGET_GOAL_DATE_KEY, goalDate)

        const goalDate1kg = formatLocalIsoDate(
          getGoalDate1kg({ weightKg, targetWeightKg, goalPaceKg: 1 }),
        )
        if (goalDate1kg) pipe.setData(TARGET_GOAL_DATE_1KG_KEY, goalDate1kg)

        const fasterGoalDate = formatLocalIsoDate(getFasterProgressDate(input))
        if (fasterGoalDate) pipe.setData(FASTER_GOAL_DATE_KEY, fasterGoalDate)

        const fasterGoalDate1kg = formatLocalIsoDate(
          getFasterProgressDate1kg({ weightKg, targetWeightKg, goalPaceKg: 1 }),
        )
        if (fasterGoalDate1kg) pipe.setData(FASTER_GOAL_DATE_1KG_KEY, fasterGoalDate1kg)

        pipe.setData(ONE_MONTH_WEIGHT_LOSS_KG_KEY, getOneMonthWeightLossKg(input))
      },
      { immediate: true },
    )

    watch(
      () => [pipe.getData(WEIGHT_KEY), pipe.getData(TARGET_WEIGHT_KEY)],
      ([w, t]) => {
        const weightKg = Number(w)
        const targetWeightKg = Number(t)
        if (!Number.isFinite(weightKg) || weightKg <= 0) return
        if (!Number.isFinite(targetWeightKg) || targetWeightKg <= 0) return
        const input = { weightKg, targetWeightKg }
        pipe.setData(DESIRED_WEIGHT_DIFF_KG_KEY, getDesiredWeightDiffKg(input))
        pipe.setData(FIRST_WEEK_LOSS_KG_KEY, getFirstWeekLossKg(input))
        pipe.setData(FIRST_WEEK_DIFF_KG_KEY, getFirstWeekDiffKg(input))
        pipe.setData(PERCENT_OF_CURRENT_WEIGHT_KEY, percentOfCurrentWeight(input))

        const weightIn2Weeks = getWeightIn2WeeksKg(input)
        if (weightIn2Weeks !== null) pipe.setData(WEIGHT_IN_2_WEEKS_KG_KEY, weightIn2Weeks)

        const bellyLegsIn2Weeks = getBellyLegsIn2Weeks(input)
        if (bellyLegsIn2Weeks) {
          pipe.setData(BELLY_IN_2_WEEKS_KEY, bellyLegsIn2Weeks.bellyIn2Weeks)
          pipe.setData(LEGS_IN_2_WEEKS_KEY, bellyLegsIn2Weeks.legsIn2Weeks)
        }
      },
      { immediate: true },
    )

    watch(
      () => pipe.getData(AGE_KEY),
      (raw) => {
        const decade = getAgeDecade({ age: Number(raw) })
        if (decade !== null) pipe.setData(AGE_GROUP_DECADE_KEY, decade)
      },
      { immediate: true },
    )

    // Daily intake amount, its fixed-width range, and the part of that range
    // the complete meals do not cover. See docs/architecture/daily-kcal.md.
    watch(
      () => [
        pipe.getData(GENDER_KEY),
        pipe.getData(AGE_KEY),
        pipe.getData(HEIGHT_KEY),
        pipe.getData(WEIGHT_KEY),
        pipe.getData(COMPLETEMEALS_COVER_KEY),
      ],
      ([gender, age, height, weight, cover]) => {
        const range = getDailyKcalRange({
          gender: typeof gender === 'string' ? gender : null,
          age: Number(age),
          heightCm: Number(height),
          weightKg: Number(weight),
        })
        if (!range) return

        pipe.setData(DAILY_KCAL_KEY, range.dailyKcal)
        pipe.setData(DAILY_KCAL_MIN_KEY, range.dailyKcalMin)
        pipe.setData(DAILY_KCAL_MAX_KEY, range.dailyKcalMax)

        const leftover = getDailyKcalLeftover(range, parseNumericVar(cover))
        pipe.setData(DAILY_KCAL_LEFTOVER_MIN_KEY, leftover.dailyKcalLeftoverMin)
        pipe.setData(DAILY_KCAL_LEFTOVER_MAX_KEY, leftover.dailyKcalLeftoverMax)
      },
      { immediate: true },
    )

    // Time from "today" (the visitor's local date, read fresh on each change)
    // until the picked event date, plus the projected weight lost by then.
    // Skips while the date is unset so prerender (empty date) writes nothing.
    watch(
      () => [
        pipe.getData(EVENT_DATE_KEY),
        pipe.getData(WEIGHT_KEY),
        pipe.getData(TARGET_WEIGHT_KEY),
        pipe.getData(GOAL_PACE_KEY),
      ],
      ([eventDateValue, weightRaw, targetWeightRaw, paceRaw]) => {
        if (typeof eventDateValue !== 'string' || !eventDateValue) return
        const weightKg = Number(weightRaw)
        const targetWeightKg = Number(targetWeightRaw)
        const pace = Number(paceRaw)
        const result = computeEventDate(
          {
            daysUntilEvent: getDaysUntil(eventDateValue),
            weightKg,
            targetWeightKg,
            goalPaceKg: Number.isFinite(pace) && pace > 0 ? pace : undefined,
          },
          eventDateParams,
        )
        pipe.setData(DAYS_UNTIL_EVENT_KEY, result.days)
        pipe.setData(WEEKS_UNTIL_EVENT_KEY, result.weeks)
        pipe.setData(MONTHS_UNTIL_EVENT_KEY, result.months)

        if (!Number.isFinite(weightKg) || weightKg <= 0) return
        if (!Number.isFinite(targetWeightKg) || targetWeightKg <= 0) return
        pipe.setData(WEIGHT_LOST_BY_EVENT_KG_KEY, result.weightLostKg)
      },
      { immediate: true },
    )

    let goalDateTickTimers: ReturnType<typeof setTimeout>[] = []

    const cancelGoalDateTick = () => {
      goalDateTickTimers.forEach(clearTimeout)
      goalDateTickTimers = []
    }

    const readGoalDateInput = (): DaysTillGoalInput | null => {
      const weightKg = Number(pipe.getData(WEIGHT_KEY))
      const targetWeightKg = Number(pipe.getData(TARGET_WEIGHT_KEY))
      if (!Number.isFinite(weightKg) || weightKg <= 0) return null
      if (!Number.isFinite(targetWeightKg) || targetWeightKg <= 0) return null
      const pace = Number(pipe.getData(GOAL_PACE_KEY))
      return {
        weightKg,
        targetWeightKg,
        goalPaceKg: Number.isFinite(pace) && pace > 0 ? pace : undefined,
        lossPace,
      }
    }

    // Countdown from targetGoalDate to fasterGoalDate, replayed on every page
    // show. See docs/architecture/variable-tick.md.
    const queueGoalDateTick = (key: string, from: Date, to: Date) => {
      const frames = buildGoalDateTickFrames(formatLocalIsoDate(from), formatLocalIsoDate(to))
      frames.forEach((value, index) => {
        goalDateTickTimers.push(
          setTimeout(() => {
            pipe.emit(PIPE_EVENTS.variableTick, {
              key,
              value,
              durationMs: GOAL_DATE_TICK_STEP_MS,
            })
          }, index * GOAL_DATE_TICK_STEP_MS),
        )
      })
    }

    const playGoalDateTick = () => {
      cancelGoalDateTick()
      const input = readGoalDateInput()
      if (!input) return

      const input1kg = { ...input, goalPaceKg: 1 }
      queueGoalDateTick(FASTER_GOAL_DATE_KEY, getGoalDate(input), getFasterProgressDate(input))
      queueGoalDateTick(
        FASTER_GOAL_DATE_1KG_KEY,
        getGoalDate1kg(input1kg),
        getFasterProgressDate1kg(input1kg),
      )
    }

    pipe.subscribe('navigation:leave', cancelGoalDateTick)
    pipe.subscribe('navigation:show', () => {
      void pipe.whenReady().then(playGoalDateTick)
    })
  },
})

export default useFitnessFunctionsIntegration
